import type { Service } from "@mirage/service-fss-bridge/service";
import provideFSSBridgeService from "@mirage/service-fss-bridge/service";
import { isEmbedOauthPage, isEmbedPage } from "@mirage/shared/util/tiny-utils";

import {
  type ConversationEventWithUuid,
  type FSSActionEvent,
  type FSSFile,
  type FSSMessageEventWithUuid,
  type UnavailableSource,
} from "@mirage/service-fss-bridge/service/bridge_types";
import type { Observable } from "rxjs";
import { BehaviorSubject, of } from "rxjs";
import {
  createIframeMessageChannel,
  type BridgeMessageChannel,
} from "./BridgeMessageChannel";

export class FSSBridgeAdapter implements Service {
  constructor(private channel: BridgeMessageChannel) {
    this.channel.addListener(this.handleMessage);
  }
  openFilePreview: (fileID: string) => Promise<void>;

  private conversationEventSubject = new BehaviorSubject<
    ConversationEventWithUuid | undefined
  >(undefined);
  private actionEventSubject = new BehaviorSubject<FSSActionEvent | undefined>(
    undefined,
  );

  private handleMessage = (eventMessage: FSSMessageEventWithUuid) => {
    switch (eventMessage.eventType) {
      case "conversation":
        this.conversationEventSubject.next(eventMessage);
        break;
      case "action":
        this.actionEventSubject.next(eventMessage);
        break;
      default: {
        // This will cause a type error when new event types are added and are not handled above.
        const exhaustiveCheck: never = eventMessage;
        return exhaustiveCheck;
      }
    }
    this.handleAck(eventMessage);
  };

  private handleAck = (eventMessage: FSSMessageEventWithUuid) => {
    this.channel.postMessage({
      eventType: "action",
      source: "ack",
      data: {
        uuid: eventMessage.uuid,
      },
    });
  };

  /**
   * Notifies FSS that the page is ready is receive messages.
   * The pathname is sent so that FSS can determine the context of the page.
   * This handles the case where there are multiple iframe embeds on the
   * same page (e.g. stacks page with sidebar open)
   */
  public readyPage = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "ready.page",
      data: {
        pathname: window.location.pathname,
      },
    });
  };

  public openAddSourceModal = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "open.source-picker",
    });
  };

  public updateConversation = async (conversationID: string) => {
    this.channel.postMessage({
      eventType: "action",
      source: "update.conversation",
      data: {
        conversationId: conversationID,
      },
    });
  };

  public closeSidebar = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "close.sidebar",
    });
  };

  public openSessionHistory = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "open.session-history",
    });
  };

  public notifyLogin = async (uid: number) => {
    this.channel.postMessage({
      eventType: "action",
      source: "notify.login",
      data: {
        userId: uid,
      },
    });
  };

  public notifyLogout = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "notify.logout",
    });
  };

  public openUnavailableSources = async (
    unavailableSources: UnavailableSource[],
  ) => {
    this.channel.postMessage({
      eventType: "action",
      source: "open.unavailable-sources",
      data: {
        unavailableSources,
      },
    });
  };

  public updateCloseButton = async (hide: boolean) => {
    this.channel.postMessage({
      eventType: "action",
      source: "update.close-button",
      data: {
        hide: hide,
      },
    });
  };

  public transcribeFiles = async (files: FSSFile[]) => {
    this.channel.postMessage({
      eventType: "action",
      source: "transcribe.files",
      data: { files },
    });
  };

  // STACKS EVENTS
  public notifyInStackDetailPage = async (
    stackId: string,
    stackName?: string,
    isChatEnabled?: boolean,
    files?: FSSFile[],
  ) => {
    this.channel.postMessage({
      eventType: "stacks",
      source: "stack_detail_page",
      data: {
        stackId,
        stackName,
        isChatEnabled,
        files,
      },
    });
  };

  public notifyInStacksPage = async () => {
    this.channel.postMessage({
      eventType: "stacks",
      source: "stacks_page",
    });
  };

  public notifyInNewStackPage = async () => {
    this.channel.postMessage({
      eventType: "stacks",
      source: "new_stack_page",
    });
  };

  public startStackChat = async (files: FSSFile[]) => {
    this.channel.postMessage({
      eventType: "stacks",
      source: "stack_chat",
      data: {
        files,
      },
    });
  };
  // STACKS EVENTS END

  // New event-based observables
  public observeConversationEvents = (): Observable<
    ConversationEventWithUuid | undefined
  > => {
    return this.conversationEventSubject;
  };

  public observeActionEvents = (): Observable<FSSActionEvent | undefined> => {
    return this.actionEventSubject;
  };

  public isSupportedEnv = () => {
    return true;
  };
}

function createNoopService(): Service {
  const noop = async () => {};
  const throwError = (method: string) => {
    throw new Error(
      `FSS Bridge is not available in this environment. Called method: ${method}`,
    );
  };

  /**
   * Guidelines:
   * - If the method is passively triggered (i.e. update, notify, etc), it should be a no-op.
   * - If the method is triggered by a user action(i.e. open, close, etc), it should throw an error.
   */
  return {
    isSupportedEnv: () => false,
    readyPage: noop,
    openAddSourceModal: () => throwError("openAddSourceModal"),
    updateConversation: noop,
    closeSidebar: () => throwError("closeSidebar"),
    openSessionHistory: () => throwError("openSessionHistory"),
    notifyLogin: noop,
    notifyLogout: noop,
    updateCloseButton: noop,
    openUnavailableSources: () => throwError("openUnavailableSources"),
    transcribeFiles: noop,
    notifyInStackDetailPage: noop,
    notifyInStacksPage: noop,
    notifyInNewStackPage: noop,
    startStackChat: noop,
    observeConversationEvents: () => of(undefined),
    observeActionEvents: () => of(undefined),
  };
}

export function setupFSSBridgeHandlers() {
  let service: Service;
  if (isEmbedOauthPage() || isEmbedPage()) {
    service = new FSSBridgeAdapter(createIframeMessageChannel());
  } else {
    // set up a Service that no-ops when Dash web is not in an iframe
    service = createNoopService();
  }

  provideFSSBridgeService(service);
}
